import history from '@history';
import { FormsPaths } from 'app/configs/routesConfig/forms';
import { formApi, formsApi } from 'app/services/api';
import _ from 'lodash';

import { FeatureFlag } from 'constants/featureFlags';
import { DOCUMENT_FILE_MIME_TYPE } from 'constants/file';
import { FormTemplateTypeNewApi } from 'constants/forms';
import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { catchAsyncActionError } from 'utils/action';
import { getFormattedDataFormsNewApi, getFormTemplateType } from 'utils/forms';
import { safeJsonParse } from 'utils/json';
import { createNamedActions } from 'utils/namedActionTypesProxy';
import { openURLInNewTab } from 'utils/navigation';

import { selectFeatureFlagByKey } from 'store/global/selectors';

export const FORM_ACTION_TYPE = createNamedActions('form', {
  UPDATE_FORM_TEMPLATE: 'UPDATE_FORM_TEMPLATE',
  GET_FORM_TEMPLATE: 'GET_FORM_TEMPLATE',
  GET_FORM_TEMPLATE_FROM_HISTORY: 'GET_FORM_TEMPLATE_FROM_HISTORY',
  GET_FORM_TEMPLATE_HISTORY: 'GET_FORM_TEMPLATE_HISTORY',
  RESET_FORM_TEMPLATE: 'RESET_FORM_TEMPLATE',
  SET_PREVIEWING_FORM_TEMPLATE: 'SET_PREVIEWING_FORM_TEMPLATE',
  SET_FORM_NAVIGATION_STATE_TYPE: 'SET_FORM_NAVIGATION_STATE_TYPE',
  SET_FORM_NAVIGATION_TYPE: 'SET_FORM_NAVIGATION_TYPE',
  GET_FORM_BY_ID: 'GET_FORM_BY_ID',
  GET_FORM_TEMPLATE_CONTENT: 'GET_FORM_TEMPLATE_CONTENT',
  GET_FORM_TEMPLATE_METADATA: 'GET_FORM_TEMPLATE_METADATA',
  GET_FORM_TEMPLATE_JSON: 'GET_FORM_TEMPLATE_JSON',
});

export const resetFormTemplateAction = () => dispatch =>
  dispatch({
    type: FORM_ACTION_TYPE.RESET_FORM_TEMPLATE,
  });

export const updateFormTemplateAction = catchAsyncActionError(
  ({ document = null, ...params }) =>
    async dispatch => {
      const { data } = await formsApi.editFormTemplate(params);

      const isPdf = [FormTemplateTypeNewApi.PDF_WITH_METADATA, FormTemplateTypeNewApi.PDF].includes(
        data?.template.type,
      );

      const templateContent = isPdf
        ? (await dispatch(getFormTemplateContentAction({ ...params, responseType: 'blob' })))
            .payload
        : null;

      const payload = {
        ...getFormattedDataFormsNewApi(data),
        template: isPdf ? templateContent : data?.template.content,
        templateJson: document,
        isTemplateSavedInPrism: !_.isNil(document),
      };

      return dispatch({
        type: FORM_ACTION_TYPE.UPDATE_FORM_TEMPLATE,
        payload,
      });
    },
);

export const getFormTemplateFromHistoryAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const isFormsConsolidationEnabled = selectFeatureFlagByKey(
      FeatureFlag.FormsConsolidationEnabled,
    )(getState());

    const historyApi = isFormsConsolidationEnabled ? formsApi : formApi;

    const { data } = await historyApi.getFormHistorySnapshotByEntryId(params);

    return dispatch({
      type: FORM_ACTION_TYPE.GET_FORM_TEMPLATE_FROM_HISTORY,
      payload: data,
    });
  },
);

const getFormByIdAction = catchAsyncActionError(params => async dispatch => {
  const {
    data: [data],
  } = await formsApi.getFormById(params);

  return dispatch({
    type: FORM_ACTION_TYPE.GET_FORM_BY_ID,
    payload: getFormattedDataFormsNewApi(data),
    formType: getFormTemplateType(data),
  });
});

const getFormTemplateMetadataAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await formsApi.getFormTemplateMetadata(params);

  return dispatch({
    type: FORM_ACTION_TYPE.GET_FORM_TEMPLATE_METADATA,
    payload: data,
  });
});

const getFormTemplateContentAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await formsApi.getFormTemplateContent(params);
  const isBlobResponse = data instanceof Blob;

  const formattedData = isBlobResponse
    ? new Blob([data], { type: DOCUMENT_FILE_MIME_TYPE.PDF })
    : data;

  return dispatch({
    type: FORM_ACTION_TYPE.GET_FORM_TEMPLATE_CONTENT,
    payload: isBlobResponse ? window.URL.createObjectURL(formattedData) : formattedData,
    meta: params,
  });
});

const getFormTemplateJsonAction = catchAsyncActionError(params => async dispatch => {
  const { data, status } = await formsApi.getFormTemplateJson(params);

  const payload =
    status === HTTP_RESPONSE_STATUS_CODES.SUCCESS ? safeJsonParse(data?.document) : null;

  return dispatch({
    type: FORM_ACTION_TYPE.GET_FORM_TEMPLATE_JSON,
    payload,
  });
});

//TODO: refactor when the app is switched to new Forms API completely
export const getFormNewApiTemplateAction = params => async dispatch => {
  const { formType, payload: data } = await dispatch(getFormByIdAction(params));
  const clone = _.clone(data);

  const isPdf = [FormTemplateTypeNewApi.PDF_WITH_METADATA, FormTemplateTypeNewApi.PDF].includes(
    formType,
  );
  const isHtmlFilled = formType === FormTemplateTypeNewApi.HTML_FILLED;

  if (formType === FormTemplateTypeNewApi.PDF_WITH_METADATA) {
    const { payload } = await dispatch(getFormTemplateMetadataAction(params));
    clone.pdfMetadata = payload;
  }

  clone.template = '';
  clone.templateJson = null;

  if (isHtmlFilled || isPdf) {
    const templateJsonPromise = isHtmlFilled
      ? dispatch(getFormTemplateJsonAction({ ...params, silent: true }))
          .then(({ payload }) => payload)
          .catch(() => null)
      : Promise.resolve(null);

    const [{ payload: template }, templateJson] = await Promise.all([
      dispatch(
        getFormTemplateContentAction({ ...params, responseType: isPdf ? 'blob' : undefined }),
      ),
      templateJsonPromise,
    ]);

    clone.template = template;
    clone.templateJson = templateJson;
  }

  clone.isTemplateSavedInPrism = !_.isNil(clone.templateJson);

  return dispatch({
    type: FORM_ACTION_TYPE.GET_FORM_TEMPLATE,
    payload: clone,
  });
};

export const openFormPreviewAction =
  ({
    formId,
    historyEntityId,
    formType,
    selectedPage,
    coborrowersNumber = 0,
    nonborrowersNumber = 0,
    loanId,
  }) =>
  () => {
    try {
      const href = history.createHref({
        pathname: FormsPaths.previewForm.generatePath({
          formId,
          historyEntityId,
          formType,
          selectedPage,
          coborrowersNumber,
          nonborrowersNumber,
          loanId,
        }),
      });

      openURLInNewTab(href);
    } catch (err) {
      return Promise.reject(err);
    }
  };

export const setFormNavigationStateTypeAction = navigationStateType => dispatch =>
  dispatch({
    type: FORM_ACTION_TYPE.SET_FORM_NAVIGATION_STATE_TYPE,
    payload: navigationStateType,
  });

export const setFormNavigationTypeAction = navigationType => dispatch =>
  dispatch({
    type: FORM_ACTION_TYPE.SET_FORM_NAVIGATION_TYPE,
    payload: navigationType,
  });
